import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { configuracoesService } from "../end-points/configuracoes";
import { personService } from "../end-points/person";
import {
  ConfiguracoesPFFormData,
  ConfiguracoesPJFormData,
  PerfilFormData,
  AlterarSenhaFormData,
  PermissionsFormData,
} from "@/pages/configuracoes/schemas";
import { toast } from "@/hooks/use-toast";

// ============================================
// QUERY KEY FACTORY
// ============================================
export const configuracoesKeys = {
  all: ["configuracoes"] as const,
  pessoaFisica: () => [...configuracoesKeys.all, "pessoa-fisica"] as const,
  pessoaJuridica: () => [...configuracoesKeys.all, "pessoa-juridica"] as const,
  documentos: () => [...configuracoesKeys.all, "documentos"] as const,
  perfil: () => [...configuracoesKeys.all, "perfil"] as const,
  permissions: () => [...configuracoesKeys.all, "permissions"] as const,
};

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Fetch configurações for Pessoa Física
 */
export const useConfiguracoesPF = () => {
  return useQuery({
    queryKey: configuracoesKeys.pessoaFisica(),
    queryFn: () => configuracoesService.getConfiguracoesPF(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================
// MUTATION HOOKS
// ============================================

/**
 * Update configurações for Pessoa Física
 */
export const useUpdateConfiguracoesPF = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConfiguracoesPFFormData) => {
      // 1. Fetch current relationships
      const meResponse = await personService.me();
      
      const requests = [];

      // 2. Profile payload
      const profileData = {
        name: data.completeName || meResponse.name,
        social_name: data.socialName || meResponse.social_name || "",
        artistic_name: data.artisticName || meResponse.artistic_name || "",
        role: data.role || meResponse.role,
        rg_number: (data.rg || meResponse.rg_number || "").replace(/\D/g, ""),
        document: (data.cpf || meResponse.document || "").replace(/\D/g, ""),
        birth_date: data.birthDate 
          ? (data.birthDate instanceof Date ? data.birthDate : new Date(data.birthDate)).toISOString() 
          : meResponse.birth_date,
        place_of_birth: data.birthPlace || meResponse.place_of_birth,
        nationality: data.nationality || meResponse.nationality,
        brazil_arrival_date: data.countryEntryDate 
          ? (data.countryEntryDate instanceof Date ? data.countryEntryDate : new Date(data.countryEntryDate)).toISOString() 
          : meResponse.brazil_arrival_date,
        id_civil_state: data.maritalStatus ? parseInt(data.maritalStatus) : meResponse.id_civil_state,
        id_pronouns: data.pronouns ? parseInt(data.pronouns) : meResponse.id_pronouns,
        cellphone_number: data.cellphone || meResponse.cellphone_number,
        tshirt_number: data.tshirtNumber || meResponse.tshirt_number || "",
      };
      
      requests.push(personService.updateProfile(profileData));
      
      // 3. Address Payload
      const pfAddress = meResponse.addresses?.find((a: any) => !a.id_person) || meResponse.addresses?.[0]; // simplistic heuristic for PF address
      const addressData = {
        id: pfAddress?.id || 0,
        neighbourhood: data.district || pfAddress?.neighbourhood || "",
        place_of_birth: data.birthPlace || meResponse.place_of_birth || "",
        address: data.residentialAddress || pfAddress?.address || "",
        postal_code: data.zipCode || pfAddress?.postal_code || "",
      };
      if (addressData.id > 0) {
        requests.push(personService.updateAddress(addressData));
      } else {
        requests.push(personService.registerAddress(addressData));
      }

      // 4. Health Payload
      if (meResponse.health) {
        const healthData = {
          id: meResponse.health.id,
          health_plan_name: data.healthPlan || meResponse.health.health_plan_name || "",
          health_plan_coverage: data.planCoverage || meResponse.health.health_plan_coverage || "",
          emergency_contact: data.emergencyContact || meResponse.health.emergency_contact || "",
          blood_type: data.bloodType || meResponse.health.blood_type || "",
          allergies: data.allergies || meResponse.health.allergies || "",
          covid_vaccine: data.coronavirusVaccination || meResponse.health.covid_vaccine || "",
          dietary_restrictions: data.dietaryRestrictions || meResponse.health.dietary_restrictions || "",
        };
        requests.push(personService.updateHealth(healthData));
      }

      // 5. Bank Payload (PF)
      const pfBank = meResponse.bank_accounts?.find((b: any) => !b.is_juridic);
      const bankData = {
        id: pfBank?.id || 0,
        id_incentive_law: 1, // Default or map if exists
        bank_name: data.bank || pfBank?.bank_name || "",
        bank_account: data.checkingAccount || pfBank?.bank_account || "",
        agency: (data.branch || pfBank?.agency) ? String(data.branch || pfBank?.agency).replace(/\D/g, "") : "",
        active: true,
        is_juridic: false,
      };
      if (bankData.id > 0) {
        requests.push(personService.updateBankDetails(bankData as any)); // as any to ignore agency type mismatch if it occurs
      } else {
        requests.push(personService.registerBankDetails(bankData as any));
      }

      await Promise.all(requests);
      return true;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.pessoaFisica() });

      if (showToast) {
        toast({
          title: "Configurações salvas",
          description: "Configurações salvas com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao salvar configurações",
          description: "Não foi possível salvar as configurações",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * Fetch configurações for Pessoa Jurídica
 */
export const useConfiguracoesPJ = () => {
  return useQuery({
    queryKey: configuracoesKeys.pessoaJuridica(),
    queryFn: () => configuracoesService.getConfiguracoesPJ(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update configurações for Pessoa Jurídica
 */
export const useUpdateConfiguracoesPJ = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ConfiguracoesPJFormData) => {
      // 1. Fetch current relationships
      const meResponse = await personService.me();
      
      const requests = [];

      // 2. Juridic payload
      const juridicData = {
        id: meResponse.juridic_person?.id || 0,
        company_name: data.razaoSocial || meResponse.juridic_person?.company_name || "",
        document: (data.cnpj || meResponse.juridic_person?.document || "").replace(/\D/g, ""),
        address: data.enderecoEmpresarial || meResponse.juridic_person?.address || "",
        neighbourhood: data.bairroPJ || meResponse.juridic_person?.neighbourhood || "",
        city_state: data.cidadeEstadoPJ || meResponse.juridic_person?.city_state || "",
        postal_code: data.cepPJ || meResponse.juridic_person?.postal_code || "",
        cnaes: data.cnaes || meResponse.juridic_person?.cnaes || "",
        municipal_register: data.inscricaoMunicipal || meResponse.juridic_person?.municipal_register || "",
        ancine_number: meResponse.juridic_person?.ancine_number || "",
      };
      
      requests.push(personService.updateJuridicPerson(juridicData));

      // 3. Bank Payload (PJ)
      const pjBank = meResponse.bank_accounts?.find((b: any) => b.is_juridic);
      const bankData = {
        id: pjBank?.id || 0,
        id_incentive_law: 1, // Default or map if exists
        bank_name: data.bancoPJ || pjBank?.bank_name || "",
        bank_account: data.contaCorrentePJ || pjBank?.bank_account || "",
        agency: (data.agenciaPJ || pjBank?.agency) ? String(data.agenciaPJ || pjBank?.agency).replace(/\D/g, "") : "",
        active: true,
        is_juridic: true,
      };
      if (bankData.id > 0) {
        requests.push(personService.updateBankDetails(bankData as any)); // as any to ignore agency type mismatch if it occurs
      } else {
        requests.push(personService.registerBankDetails(bankData as any));
      }

      await Promise.all(requests);
      return true;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.pessoaJuridica() });

      if (showToast) {
        toast({
          title: "Configurações salvas",
          description: "Configurações de Pessoa Jurídica salvas com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao salvar configurações",
          description: "Não foi possível salvar as configurações",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * Fetch documents
 */
export const useDocuments = () => {
  return useQuery({
    queryKey: configuracoesKeys.documentos(),
    queryFn: () => configuracoesService.getDocuments(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Upload document
 */
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fieldName, file }: { fieldName: string; file: File }) =>
      configuracoesService.uploadDocument(fieldName, file),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.documentos() });

      toast({
        title: "Documento enviado",
        description: "Documento enviado com sucesso",
        variant: "success",
      });
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Erro ao enviar documento",
        description: "Não foi possível enviar o documento",
        variant: "destructive",
      });
    },
  });
};

/**
 * Fetch perfil data
 */
export const usePerfil = () => {
  return useQuery({
    queryKey: configuracoesKeys.perfil(),
    queryFn: () => configuracoesService.getPerfil(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update perfil
 */
export const useUpdatePerfil = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PerfilFormData) =>
      configuracoesService.updatePerfil(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.perfil() });

      if (showToast) {
        toast({
          title: "Perfil atualizado",
          description: "Perfil atualizado com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao atualizar perfil",
          description: "Não foi possível atualizar o perfil",
          variant: "destructive",
        });
      }
    },
  });
};

/**
 * Upload profile image
 */
export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => configuracoesService.uploadProfileImage(file),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.perfil() });

      toast({
        title: "Imagem atualizada",
        description: "Imagem de perfil atualizada com sucesso",
        variant: "success",
      });
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Erro ao atualizar imagem",
        description: "Não foi possível atualizar a imagem de perfil",
        variant: "destructive",
      });
    },
  });
};

/**
 * Change password
 */
export const useChangePassword = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: (data: AlterarSenhaFormData) =>
      configuracoesService.changePassword(data),
    onSuccess: () => {
      toast({
        title: "Senha alterada",
        description: "Senha alterada com sucesso",
        variant: "success",
      });

      // Call the callback to open success modal
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Erro ao alterar senha",
        description: "Não foi possível alterar a senha. Verifique se a senha antiga está correta.",
        variant: "destructive",
      });
    },
  });
};

/**
 * Fetch permissions
 */
export const usePermissions = () => {
  return useQuery({
    queryKey: configuracoesKeys.permissions(),
    queryFn: () => configuracoesService.getPermissions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update permissions
 */
export const useUpdatePermissions = (showToast: boolean = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PermissionsFormData) =>
      configuracoesService.updatePermissions(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: configuracoesKeys.permissions() });

      if (showToast) {
        toast({
          title: "Permissões salvas",
          description: "Permissões salvas com sucesso",
          variant: "success",
        });
      }
    },
    onError: (err) => {
      console.error(err);
      if (showToast) {
        toast({
          title: "Erro ao salvar permissões",
          description: "Não foi possível salvar as permissões",
          variant: "destructive",
        });
      }
    },
  });
};
